import React, { createContext, useContext, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const InactivityContext = createContext();

export const InactivityProvider = ({ children }) => {
  const timeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Properly check authentication using the saved authData
  const isAuthenticated = () => {
    const authData = localStorage.getItem("authData");
    if (!authData) return false;
    try {
      const parsed = JSON.parse(authData);
      return !!(parsed?.token || parsed?.userToken);
    } catch {
      return false;
    }
  };

  // ✅ Public routes where inactivity timeout / redirect should not trigger
  const publicRoutes = [
    "/",
    "/login-1",
    "/user-pages/forgot-password",
    "/user-pages/lockscreen",
  ];

  const logoutUser = () => {
    alert("Session expired due to inactivity.");
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(logoutUser, 10 * 60 * 1000); // 10 minutes
  };

  // ✅ Reset inactivity timer only on private routes
  useEffect(() => {
    const events = ["mousemove", "keydown", "scroll", "click"];

    const handleActivity = () => {
      if (!publicRoutes.includes(location.pathname)) resetTimeout();
    };

    events.forEach((event) => window.addEventListener(event, handleActivity));

    if (!publicRoutes.includes(location.pathname)) resetTimeout();

    return () => {
      clearTimeout(timeoutRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
    };
  }, [location.pathname]);

  // ✅ Prevent back navigation to protected routes if not authenticated
  useEffect(() => {
    const handlePopState = () => {
      if (!isAuthenticated() && !publicRoutes.includes(location.pathname)) {
        navigate("/", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate, location.pathname]);

  return (
    <InactivityContext.Provider value={{ resetTimeout }}>
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivity = () => useContext(InactivityContext);
